import CryptoJS from "crypto-js";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createUserAccount,
  initiateSavingBusinessData,
  loginUser,
} from "../../services/apiService";
import { setToken, setUserInfo } from "../../utils/auth";
import BasicBusinessInfo from "./BasicBusinessInfo";
import BusinessLogo from "./BusinessLogo";
import OfferingTypeInfo from "./OfferingTypeInfo";
import PagesInfo from "./PagesInfo";
import ProductsInfo from "./ProductsInfo";
import ServicesInfo from "./ServicesInfo";
import "./SingleFormBuilder.css";
import WebPagesInfo from "./WebPagesInfo";
import WebsiteCountry from "./WebsiteCountry";
import WebsitePreferredLanguage from "./WebsitePreferredLanguage";
import styled from "styled-components";
import logo from "../../casualtech-logo.png"

const Logo = styled.img`
  width: 100px;
  height: auto;
  border-radius: 10px;
`;

function Form() {
  const navigate = new useNavigate();
  const { currentLanguage, preferredLanguage, languageFullName, region } =
    useSelector((state) => state.settings);

  const [successfullySaved, setSuccessfullySaved] = useState(false);

  const [requiredLanguage, setRequiredLanguage] = useState("");
  const [requiredOfferingType, setRequiredOfferingType] = useState("");
  const [contactNumber, setContactNumber] = useState("");

  const [formData, setFormData] = useState({
    basicBusinessInfo: {},
    businessLogoInfo: {},
    webPages: [],
    pagesInfo: {},
    products: [],
    services: [],
  });

  const handleBasicBusinessInfoChange = (basicBusinessInfo) => {
    setFormData({ ...formData, basicBusinessInfo });
  };

  const handleBusinessLogoChange = (businessLogoInfo) => {
    setFormData({ ...formData, businessLogoInfo });
  };

  const handleWebPagesInfoChange = (webPages) => {
    setFormData({ ...formData, webPages });
  };

  const handlePagesInfoChange = (pagesInfo) => {
    setFormData({ ...formData, pagesInfo });
  };

  const handleLanguageInfoChange = (languageInfo) => {
    setRequiredLanguage(languageInfo.language);
  };

  const handleOfferingInfoChange = (offeringInfo) => {
    setRequiredOfferingType(offeringInfo.offering_type);
    setFormData({ ...formData, offeringInfo });
  };

  const handleServicesInfoChange = (services) => {
    setFormData({ ...formData, services });
  };

  const handleProductsInfoChange = (products) => {
    setFormData({ ...formData, products });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const businessName = formData?.basicBusinessInfo?.businessName?.trim();
    const contactPersonName =
      formData?.basicBusinessInfo?.contactPersonName?.trim();
    const businessEmail = formData?.basicBusinessInfo?.businessEmail?.trim();
    const offeringType = formData?.offeringInfo?.offering_type?.trim();

    if (!businessName || !contactPersonName || !contactNumber || !businessEmail || !offeringType || 
      formData?.webPages?.length < 0
    ) {
      toast.error("Please fill the complete form.");
      return false;
    }

    let payloadData = {
      language: languageFullName,
      websiteName: formData.basicBusinessInfo.businessName,
      contactName: formData.basicBusinessInfo.contactPersonName,
      contactNumber: contactNumber,
      email: formData.basicBusinessInfo.businessEmail,
      logo: formData.businessLogoInfo,
      webPages: formData.webPages,
      homePageContent: formData.pagesInfo.home,
      aboutUsPageContent: formData.pagesInfo.about,
      reachToUs: formData.pagesInfo.contact,
      offering: formData.offeringInfo.offering_type,
      products: formData.products,
      services: formData.services,
    };

    registerBusiness(payloadData);
  };

  async function registerBusiness(payloadData) {
    try {
      const firstPartOfEmail = payloadData.email.split("@");
      let generatedPassword = firstPartOfEmail[0] + "#" + Math.floor(1000 + Math.random() * 9000);

      /**
       * Create Account
       */
      let createAccountResult = await createUserAccount(payloadData.email);

      if (createAccountResult.result == "success") {
        /**
         * Login User
         */

        let loginResult = await loginUser(payloadData.email, generatedPassword);
        if (loginResult.result == "success") {
          const loginToken = loginResult.data.token;
          setToken(loginToken);

          const userData = {
            role: loginResult.data.role,
            email: loginResult.data.email,
          };
          const ENCRYPT_DECRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

          const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(userData),
            ENCRYPT_DECRYPTION_KEY
          ).toString();

          setUserInfo(encryptedData);

          /**
           * Initiate Business Account
           */
          let res = await initiateSavingBusinessData(payloadData, loginToken);

          if (res.result == "success") {
            toast.success(res.message);
            navigate("/dashboard");
          } else {
            toast.error(res.message);
          }
        } else if (loginResult.result == "error") {
          toast.error(loginResult.message);
          return false;
        }
      } else if (createAccountResult.result == "error") {
        toast.error(createAccountResult.message);
        return false;
      }
    } catch (error) {
      toast.error("Error fetching users:" + error);
      return false;
    }
  }

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-17843273376/fThACLuKs9obEKD9qrxC",
      });
    }
  }, []);


  return (
    <div className="container">
      {!successfullySaved && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <div className="brand">
                <Logo src={logo} alt="Logo" />
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} id="businessForm" className="panel p-4">
            <div className="row g-3">
              <WebsiteCountry onChange={handleLanguageInfoChange} />
              <WebsitePreferredLanguage />
              <BasicBusinessInfo
                onChange={handleBasicBusinessInfoChange}
                getContactNumber={setContactNumber}
              />
              <BusinessLogo onChange={handleBusinessLogoChange} />
              <WebPagesInfo onChange={handleWebPagesInfoChange} />
              <PagesInfo
                onChange={handlePagesInfoChange}
                selectedPages={formData.webPages}
              />

              <OfferingTypeInfo onChange={handleOfferingInfoChange} />

              {requiredOfferingType == "services" ? (
                <ServicesInfo
                  onChange={handleServicesInfoChange}
                  savedServices={formData.services}
                />
              ) : requiredOfferingType == "products" ? (
                <ProductsInfo
                  onChange={handleProductsInfoChange}
                  savedProducts={formData.products}
                />
              ) : requiredOfferingType == "both" ? (
                <>
                  <div style={{ marginBottom: "20px" }}>
                    <ProductsInfo
                      onChange={handleProductsInfoChange}
                      savedProducts={formData.products}
                    />
                  </div>
                  <div>
                    <ServicesInfo
                      onChange={handleServicesInfoChange}
                      savedServices={formData.services}
                    />
                  </div>
                </>
              ) : null}
            </div>

            <div className="mt-4 d-flex justify-content-end">
              <button
                id="saveFooterBtn"
                className="btn btn-lg btn-success me-2"
                type="submit"
              >
                <i className="bi bi-check-lg"></i> Save All Changes
              </button>
              {/* <button
                id="previewFooterBtn"
                type="button"
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-eye"></i> Preview
              </button> */}
            </div>
          </form>
          <footer className="mt-4 p-3 text-center small-muted rounded-3 fixed-bottom-card">
            © 2025 Casual Tech — Premium Site Builder
          </footer>
        </>
      )}

      {successfullySaved && (
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card">
              <header className="card-header note">
                <h4 className="card-title mt-2 ">Casual Tech</h4>
              </header>
              <article className="card-body">
                <div className="card" style={{ width: "50rem" }}>
                  <div className="card-body">
                    <h3 className="card-title">Saved, Successfully.</h3>

                    <p className="card-text">We will get back to you soon.</p>
                    <Link to={`/dashboard`} className="card-link">
                      My Dashboard
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Form;
